import { Link } from "react-router-dom";
import { PLANS } from "../lib/funding";
import { Check, ArrowRight, FileText, Bot, Download, Sparkles, Rocket, Shield } from "lucide-react";

const STEPS = [
  { num: "01", title: "Fill Out the Form", body: "Submit your details in our secure online intake form. Personal credentials, business profile, funding goals.", icon: FileText },
  { num: "02", title: "Run Funding Bots & AI Agents", body: "Our AI analyzes your profile against thousands of lenders, matching funding qualifications, NAICS codes, and certifications.", icon: Bot },
  { num: "03", title: "Download Your Report", body: "Receive your personalized funding plan of action — credit strategy, lender list, and step-by-step compliance roadmap.", icon: Download },
];

function TierCard({ tier, planId }) {
  return (
    <div
      data-testid={`funding-tier-${tier.id}`}
      className="relative flex flex-col p-10"
      style={{
        border: tier.featured ? "1px solid var(--brass)" : "1px solid var(--hairline)",
        background: tier.featured ? "var(--surface)" : "transparent",
      }}
    >
      {tier.featured && (
        <div className="absolute -top-3 left-10 px-3 py-1 text-[0.625rem] uppercase tracking-[0.22em]" style={{ background: "var(--bg)", color: "var(--brass)", border: "1px solid var(--brass)" }}>
          Most Popular · Best Value
        </div>
      )}
      <p className="eyebrow mb-3">{tier.tagline}</p>
      <h3 className="font-display text-4xl mb-2">{tier.name}</h3>
      <p className="text-sm mb-6" style={{ color: "var(--ink-muted)" }}>{tier.headline}</p>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-display text-5xl" style={{ color: "var(--brass)" }}>{tier.price}</span>
        <span className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--ink-faint)" }}>{tier.cadence}</span>
      </div>
      {tier.annualPrice && (
        <p className="text-xs uppercase tracking-[0.18em] mb-3" style={{ color: "var(--ink-muted)" }}>
          or <span style={{ color: "var(--brass)" }}>{tier.annualPrice}</span> {tier.annualCadence} · {tier.annualSavings}
        </p>
      )}
      {tier.trialNote && (
        <p className="text-xs uppercase tracking-[0.18em] mb-3" style={{ color: "var(--brass)" }}>
          ✦ {tier.trialNote}
        </p>
      )}
      <hr className="hairline my-6" />
      <ul className="space-y-3 mb-10 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm leading-relaxed">
            <Check size={14} className="mt-1 shrink-0" style={{ color: "var(--brass)" }} />
            <span style={{ color: "var(--ink)" }}>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to={`/funding/apply?plan=${planId}`}
        data-testid={`apply-${tier.id}`}
        className={tier.featured ? "btn-brass w-full" : "btn-ghost w-full"}
        style={{ justifyContent: "center" }}
      >
        {tier.cta} <ArrowRight size={14} />
      </Link>
    </div>
  );
}

export default function Funding() {
  return (
    <div data-testid="funding-page" className="pt-24">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-16">
        <div className="text-center max-w-4xl mx-auto reveal">
          <p className="eyebrow mb-5">Wealth First Inc. · Funding Portal</p>
          <h1 className="font-display text-6xl md:text-7xl lg:text-8xl mb-8 leading-[0.95]">
            Capital, on the <em style={{ color: "var(--brass)" }}>record</em>. <br />
            Funding, by <em style={{ color: "var(--brass)" }}>design</em>.
          </h1>
          <p className="text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-10" style={{ color: "var(--ink-muted)" }}>
            AI-powered funding analysis. Credit-building automation. Lender-matching agents.
            Built for founders who want to move from intention to capital with discipline.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/funding/apply?plan=autopilot" data-testid="funding-hero-cta" className="btn-brass">
              Start 7-Day Free Trial <Rocket size={14} />
            </Link>
            <Link to="/funding/compare" className="btn-ghost">Compare Plans</Link>
          </div>
        </div>
      </section>

      <hr className="hairline max-w-[1400px] mx-auto" />

      {/* HOW IT WORKS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="how-it-works">
        <div className="mb-16">
          <p className="eyebrow mb-3">How It Works</p>
          <h2 className="font-display text-5xl md:text-6xl max-w-2xl">From form to funding, in three quiet steps.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.num} className="relative">
                <p className="font-display text-7xl mb-4" style={{ color: "var(--brass)", opacity: 0.4 }}>{s.num}</p>
                <Icon size={28} style={{ color: "var(--brass)" }} className="mb-5" />
                <h3 className="font-display text-3xl mb-3">{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--ink-muted)" }}>{s.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <hr className="hairline max-w-[1400px] mx-auto" />

      {/* PRICING TIERS */}
      <section id="pricing" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-center mb-16">
          <p className="eyebrow mb-3">Transparent Pricing</p>
          <h2 className="font-display text-5xl md:text-6xl mb-6">Three quiet paths to capital.</h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: "var(--ink-muted)" }}>
            One-time analysis, ongoing membership, or full automation. Cancel or downgrade anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <TierCard tier={PLANS.ai_report} planId="ai_report" />
          <TierCard tier={PLANS.pro} planId="pro_monthly" />
          <TierCard tier={PLANS.autopilot} planId="autopilot" />
        </div>

        <div className="text-center mt-12">
          <Link to="/funding/compare" data-testid="compare-link" className="brass-link text-sm uppercase tracking-[0.22em]">
            View Full Comparison <ArrowRight size={12} className="inline ml-1" />
          </Link>
        </div>
      </section>

      <hr className="hairline max-w-[1400px] mx-auto" />

      {/* VALUE STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="flex flex-col gap-3">
            <Sparkles size={24} style={{ color: "var(--brass)" }} />
            <h4 className="font-display text-2xl">AI-Powered Throughout</h4>
            <p className="text-sm" style={{ color: "var(--ink-muted)" }}>
              Funding bots and credit agents work continuously — not just at signup.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Shield size={24} style={{ color: "var(--brass)" }} />
            <h4 className="font-display text-2xl">Fiduciary Discipline</h4>
            <p className="text-sm" style={{ color: "var(--ink-muted)" }}>
              Revocable trust setup, NAICS scanning, and compliance baked into AutoPilot.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Rocket size={24} style={{ color: "var(--brass)" }} />
            <h4 className="font-display text-2xl">Built to Scale</h4>
            <p className="text-sm" style={{ color: "var(--ink-muted)" }}>
              From single-LLC founders to multi-entity family offices.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
